/* eslint-disable react/prop-types */
/**
 * TillBadge — Sprint 2 B.2 correction 5.
 *
 * The single source of truth for "whose till is this and how long has
 * it been open". Renders three visual states:
 *
 *   • Green pill      — till is open and fresh (< warning threshold)
 *   • Amber pill      — till is open past the tenant threshold
 *   • Red pill        — no till open
 *
 * Amber is ADVISORY ONLY. Nothing depends on it functionally; the
 * settle gate still runs on hasOpenTill regardless. The threshold is
 * `state.session.till_open_warning_hours` (default 4h, tenant-scoped).
 *
 * Two skins:
 *   • `theme="dark"`  — for the FloorView dark-green top bar
 *   • `theme="light"` — for the BillingScreen white top bar
 */
import React, { useEffect, useState } from "react";

export default function TillBadge({ state, userName, theme = "light" }) {
  const till           = state?.session?.current_till;
  const warnH          = Number(state?.session?.till_open_warning_hours) || 4;

  // Re-render every 60s so the amber threshold flips without a full
  // page interaction. Cheap — the effect only mounts when a till is
  // open and the timer is destroyed on unmount / close.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!till?.opened_at) return undefined;
    const iv = setInterval(() => tick((t) => t + 1), 60_000);
    return () => clearInterval(iv);
  }, [till?.opened_at]);

  if (till) {
    const floatAmt = Number(till.opening_float || 0);
    const openedMs = Date.parse(till.opened_at || "");
    const hoursOpen = Number.isFinite(openedMs)
      ? (Date.now() - openedMs) / 3_600_000
      : 0;
    const stale = hoursOpen >= warnH;
    const tillOwner = till.staff_name || userName;
    const durLabel  = stale ? ` · ${Math.floor(hoursOpen)}h open` : "";
    const tip = stale
      ? `Till open ${Math.floor(hoursOpen)}h · ${tillOwner} · float ₹${floatAmt.toLocaleString("en-IN")} — consider closing`
      : `Till open · ${tillOwner} · float ₹${floatAmt.toLocaleString("en-IN")}`;
    const classes = stale
      ? (theme === "dark"
          ? "bg-amber-400/25 border-amber-300/80 text-amber-50"
          : "bg-amber-50 border-amber-400 text-amber-900")
      : (theme === "dark"
          ? "bg-emerald-500/15 border-emerald-400/60 text-emerald-100"
          : "bg-emerald-50 border-emerald-300 text-emerald-800");
    const dot = stale ? "bg-amber-300" : (theme === "dark" ? "bg-emerald-400" : "bg-emerald-500");
    return (
      <div className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-[10px] whitespace-nowrap ${classes}`}
           data-testid="pos-till-badge-open"
           data-till-stale={stale ? "true" : "false"}
           title={tip}>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot} ${stale ? "animate-pulse" : ""}`} />
        <span className="uppercase tracking-wider font-semibold">Till</span>
        <span className={theme === "dark" ? "text-white font-semibold" : "font-semibold"}>{tillOwner}</span>
        <span className={`tabular-nums ${theme === "dark" ? "text-emerald-200/90" : ""}`}>· ₹{floatAmt.toLocaleString("en-IN")}</span>
        {stale && <span className="uppercase tracking-wider font-semibold" data-testid="pos-till-badge-stale">{durLabel}</span>}
      </div>
    );
  }

  // No till — cashier has not opened one yet.
  const noneClasses = theme === "dark"
    ? "bg-rose-500/20 border-rose-400/70 text-rose-100"
    : "bg-rose-50 border-rose-300 text-rose-800";
  const dotNone = theme === "dark" ? "bg-rose-300" : "bg-rose-500";
  const title = "No till open — open one from the login prompt to start settling";
  return (
    <div className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full border text-[10px] whitespace-nowrap font-semibold uppercase tracking-wider ${noneClasses}`}
         data-testid="pos-till-badge-none"
         title={title}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotNone}`} />
      <span>No till open</span>
    </div>
  );
}
